/**
 * Read-only billing snapshot for the Service Request tracker drawer: Stripe cards + FHIR Coverage.
 */

import { Alert, Badge, Card, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconCreditCard, IconShield } from '@tabler/icons-react';
import type { Coverage } from '@medplum/fhirtypes';
import type { SavedPaymentMethod } from '../../../lib/stripeBillingService';

function formatExpiry(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${year}`;
}

function coveragePayer(c: Coverage): string {
  const p = c.payor?.[0];
  if (p?.display) return p.display;
  const ref = p?.reference ?? '';
  if (ref.startsWith('Organization/')) return ref;
  return 'Insurance';
}

function coverageMemberId(c: Coverage): string | undefined {
  const fromId = c.identifier?.map((i) => i.value).filter(Boolean).join(', ');
  if (fromId) return fromId;
  return c.subscriberId ?? undefined;
}

export interface TrackerDrawerLiveBillingProps {
  loading: boolean;
  error: string | null;
  paymentMethods: SavedPaymentMethod[];
  coverages: Coverage[];
}

export function TrackerDrawerLiveBilling({
  loading,
  error,
  paymentMethods,
  coverages,
}: TrackerDrawerLiveBillingProps): JSX.Element {
  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading billing and coverage…
        </Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Could not load billing data">
        {error}
      </Alert>
    );
  }

  const hasAny = paymentMethods.length > 0 || coverages.length > 0;

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={4}>Card on file</Title>
            <Text size="sm" c="dimmed" mt={4}>
              From Stripe (intake or portal)
            </Text>
          </div>
          <IconCreditCard size={20} color="var(--mantine-color-gray-6)" />
        </Group>
        {paymentMethods.length === 0 ? (
          <Text size="sm" c="dimmed">
            No saved payment method for this client yet.
          </Text>
        ) : (
          <Stack gap="sm">
            {paymentMethods.map((pm) => (
              <Paper key={pm.stripePaymentMethodId} p="md" withBorder>
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text size="sm" fw={600}>
                      {pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} •••• {pm.last4}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Expires {formatExpiry(pm.expMonth, pm.expYear)}
                      {pm.cardholderName ? ` · ${pm.cardholderName}` : ''}
                    </Text>
                  </div>
                  {pm.isDefault && (
                    <Badge size="sm" variant="light" color="green">
                      Default
                    </Badge>
                  )}
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Card>

      <Card withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={4}>Insurance coverage</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Coverage on file in Medplum (e.g. from intake)
            </Text>
          </div>
          <IconShield size={20} color="var(--mantine-color-gray-6)" />
        </Group>
        {coverages.length === 0 ? (
          <Text size="sm" c="dimmed">
            No Coverage resources for this patient. Cash pay or not yet entered.
          </Text>
        ) : (
          <Stack gap="sm">
            {coverages.map((c) => (
              <Paper key={c.id ?? JSON.stringify(c.identifier)} p="md" withBorder>
                <Text size="sm" fw={600}>
                  {coveragePayer(c)}
                </Text>
                {coverageMemberId(c) && (
                  <Text size="xs" c="dimmed" mt={4}>
                    Member / subscriber ID: {coverageMemberId(c)}
                  </Text>
                )}
                <Group gap="xs" mt="xs">
                  <Badge size="sm" variant="light">
                    {c.status ?? 'active'}
                  </Badge>
                  {c.period?.start && (
                    <Text size="xs" c="dimmed">
                      Period: {c.period.start}
                      {c.period.end ? ` – ${c.period.end}` : ''}
                    </Text>
                  )}
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Card>

      {!hasAny && (
        <Text size="xs" c="dimmed">
          Eligibility verification workflows (VOB) are managed from the full client chart when wired.
        </Text>
      )}
    </Stack>
  );
}
